import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * Auth store — tracks invite params that need to be processed after login.
 *
 * Note: actual auth state (is user logged in?) is determined by the presence
 * of the `af-auth` cookie, which is set server-side alongside `access_token`.
 * This avoids stale state after OAuth callbacks or server-side token expiry.
 */
export const useAuthStore = defineStore(
  'auth',
  () => {
    /** Invite query params stored when the user clicks an invite link while logged out. */
    const pendingInviteParams = ref<Record<string, string> | null>(null);

    /**
     * In-app route to push to after invitations are confirmed, overriding the
     * UserFlowService redirect. Set during business register so the success page
     * is shown even when the user had an invitation to accept first.
     * Consumed (read + cleared) once by the invitation views.
     */
    const postInvitationRedirect = ref<string | null>(null);

    /**
     * Email to pre-fill on the login form, set from the `af-email-exists` cookie
     * the screening token flow drops just before redirecting to /login.
     */
    const prefillEmail = ref<string | null>(null);

    function setPendingInviteParams(params: Record<string, string> | null) {
      pendingInviteParams.value = params;
    }

    /** Consume and clear the pending params in one call. */
    function consumeInviteParams(): Record<string, string> | null {
      const params = pendingInviteParams.value;
      pendingInviteParams.value = null;
      return params;
    }

    function setPostInvitationRedirect(value: string | null) {
      postInvitationRedirect.value = value;
    }

    /** Consume and clear the post-invitation redirect in one call. */
    function consumePostInvitationRedirect(): string | null {
      const value = postInvitationRedirect.value;
      postInvitationRedirect.value = null;
      return value;
    }

    function setPrefillEmail(value: string | null) {
      prefillEmail.value = value;
    }

    function consumePrefillEmail(): string | null {
      const value = prefillEmail.value;
      prefillEmail.value = null;
      return value;
    }

    return {
      pendingInviteParams,
      setPendingInviteParams,
      consumeInviteParams,
      postInvitationRedirect,
      setPostInvitationRedirect,
      consumePostInvitationRedirect,
      prefillEmail,
      setPrefillEmail,
      consumePrefillEmail
    };
  },
  {
    persist: {
      storage: localStorage,
      pick: ['pendingInviteParams', 'postInvitationRedirect', 'prefillEmail']
    }
  }
);
